import React, { useEffect, useRef, useState } from "react";
import { Button, Checkbox, Modal, Typography, message } from "antd";
import { ExternalLink, ShieldCheck } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { setCurrentUser, setPolicyAcceptance } from "../../Actions/user.action";
import { accept_policies, record_disclaimer_displayed } from "../../Services/user.service";
import "./PolicyAcceptanceModal.css";

const { Text, Title } = Typography;

const PolicyAcceptanceModal = ({ visible }) => {
    const { t } = useTranslation("users");
    const dispatch = useDispatch();
    const currentUser = useSelector((state) => state.users.currentUser);
    const termsVersion = useSelector((state) => state.users.terms_version);
    const privacyVersion = useSelector((state) => state.users.privacy_version);
    const termsUrl = useSelector((state) => state.users.terms_url);
    const privacyUrl = useSelector((state) => state.users.privacy_url);
    const disclaimerVersion = useSelector((state) => state.users.disclaimer_version);
    const policyAcceptanceText = useSelector((state) => state.users.policy_acceptance_text);
    const hasPreviousPolicyAcceptance = useSelector((state) => state.users.has_previous_policy_acceptance);
    const policyAcceptanceReason = useSelector((state) => state.users.policy_acceptance_reason);
    const displayedKeyRef = useRef(null);
    const [checked, setChecked] = useState(false);
    const [loading, setLoading] = useState(false);
    const isPolicyUpdate = Boolean(
        hasPreviousPolicyAcceptance || policyAcceptanceReason === "updated_terms"
    );
    const modalTitle = isPolicyUpdate
        ? t("policyAcceptance.updatedTitle")
        : t("policyAcceptance.title");
    const modalBody = isPolicyUpdate
        ? t("policyAcceptance.updatedBody")
        : (policyAcceptanceText || t("policyAcceptance.body"));

    useEffect(() => {
        if (!visible || !disclaimerVersion) return;

        const displayedKey = `${currentUser?.id || "current"}:${disclaimerVersion}`;
        if (displayedKeyRef.current === displayedKey) return;
        displayedKeyRef.current = displayedKey;

        record_disclaimer_displayed().catch(() => {
            displayedKeyRef.current = null;
        });
    }, [currentUser?.id, disclaimerVersion, visible]);

    const handleAccept = async () => {
        if (!checked) return;

        setLoading(true);
        try {
            const result = await accept_policies();
            const payload = {
                ...(result?.data || {}),
                requires_policy_acceptance: false,
            };
            dispatch(setPolicyAcceptance(payload));
            dispatch(setCurrentUser({
                ...currentUser,
                ...payload,
            }));
            message.success(t("policyAcceptance.success"));
        } catch (error) {
            message.error(
                error?.response?.data?.message ||
                error?.response?.data?.detail ||
                t("policyAcceptance.errors.accept")
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            open={visible}
            footer={null}
            closable={false}
            maskClosable={false}
            keyboard={false}
            centered
            width={560}
            className="policy-acceptance-modal"
        >
            <div className="policy-acceptance-shell">
                <div className="policy-acceptance-icon" aria-hidden="true">
                    <ShieldCheck size={22} strokeWidth={1.8} />
                </div>

                <div className="policy-acceptance-content">
                    <Text className="policy-acceptance-kicker">
                        {isPolicyUpdate ? t("policyAcceptance.updatedKicker") : t("policyAcceptance.kicker")}
                    </Text>
                    <Title level={3} className="policy-acceptance-title">
                        {modalTitle}
                    </Title>
                    <Text className="policy-acceptance-copy">
                        {modalBody}
                    </Text>

                    <div className="policy-acceptance-meta" aria-label={t("policyAcceptance.versionLabel")}>
                        <span>
                            {t("policyAcceptance.termsVersion", { version: termsVersion })}
                        </span>
                        <span>
                            {t("policyAcceptance.privacyVersion", { version: privacyVersion })}
                        </span>
                    </div>

                    <div className="policy-acceptance-links">
                        <a href={termsUrl || t("policyAcceptance.links.termsUrl")} target="_blank" rel="noreferrer">
                            {t("policyAcceptance.links.terms")}
                            <ExternalLink size={13} strokeWidth={1.8} />
                        </a>
                        <a href={privacyUrl || t("policyAcceptance.links.privacyUrl")} target="_blank" rel="noreferrer">
                            {t("policyAcceptance.links.privacy")}
                            <ExternalLink size={13} strokeWidth={1.8} />
                        </a>
                    </div>

                    <Checkbox
                        className="policy-acceptance-checkbox"
                        checked={checked}
                        onChange={(event) => setChecked(event.target.checked)}
                    >
                        {t("policyAcceptance.checkbox")}
                    </Checkbox>

                    <Button
                        type="primary"
                        size="large"
                        block
                        disabled={!checked}
                        loading={loading}
                        onClick={handleAccept}
                        className="policy-acceptance-button"
                    >
                        {t("policyAcceptance.acceptButton")}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default PolicyAcceptanceModal;
